import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';

import useAlerts from '../../../hooks/order/useAlerts';
import globalTranslations from '../../../i18n/globalTranslations';
import Order from '../../../models/order/Order';
import ProductImageModel from '../../../models/ProductImage';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import ProductClass from '../../../types/ProductClass';
import ProductTypeClass from '../../../types/ProductTypeClass';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import OrderTotals from '../../order/OrderTotals';
import ProductImage from '../../product/ProductImage';
import ProductWarranty from '../../product/ProductWarranty';
import { OrderHeaders } from '../MyAccountOrder';
import OrderCloneButton from '../../order/OrderCloneButton';
import RequestState from '../../../types/RequestState';
import CartStore from '../../../store/CartStore';

export const MyAccountOrderProducts = ({
  accountStore,
  cartStore,
  configStore,
  uiStore,
  order,
}) => {
  const { isMobile } = uiStore;
  const { commonAlerts } = order;

  const { renderGeneralAlerts } = useAlerts({
    generalAlerts: commonAlerts.general,
  });

  const structureBundleIds = new Set(
    order.products
      .filter(
        (product) =>
          product.class === ProductClass.STRUCTURE && !!product.bundle_id
      )
      .map((product) => product.bundle_id)
  );

  const structureChildrenByBundle = order.products.reduce(
    (bundles, product) => {
      if (
        !!product.bundle_id &&
        structureBundleIds.has(product.bundle_id) &&
        product.class !== ProductClass.STRUCTURE
      ) {
        const children = bundles[product.bundle_id] || [];
        children.push(product);
        bundles[product.bundle_id] = children;
      }

      return bundles;
    },
    {}
  );
  const attachedStructureChildrenBundles = new Set();
  const orderedProducts = order.products.reduce((products, product) => {
    const isStructureParent =
      product.class === ProductClass.STRUCTURE && !!product.bundle_id;
    const isStructureChild =
      !!product.bundle_id &&
      structureBundleIds.has(product.bundle_id) &&
      product.class !== ProductClass.STRUCTURE;

    if (isStructureParent) {
      // Storage-split parents share bundle_id; only nest children under the
      // first parent so the child list is not repeated for each storage row.
      const children = attachedStructureChildrenBundles.has(product.bundle_id)
        ? []
        : structureChildrenByBundle[product.bundle_id] || [];
      attachedStructureChildrenBundles.add(product.bundle_id);

      return [...products, product, ...children];
    }

    if (isStructureChild) {
      return products;
    }

    return [...products, product];
  }, []);

  const renderProductHeaderCol = (label, sizes) => {
    const actualSizes = Object.assign(
      {
        xs: 4,
      },
      sizes
    );
    return (
      <Col className="MyAccountOrder__product-header" {...actualSizes}>
        {label}
      </Col>
    );
  };

  const getProductLink = (product) => {
    return product.url;
  };

  const shouldRenderProductImage = (product) => {
    return Boolean(product.image_url || getProductLink(product));
  };

  const renderProductLink = (product) => {
    return getProductLink(product) ? (
      <MaybeExternalLink to={product.url}>{product.name}</MaybeExternalLink>
    ) : (
      <span className="MyAccountOrder__products-name">{product.name}</span>
    );
  };

  const hasValue = (value) => value != null && value !== '';

  const showManufacturersCode =
    configStore.productPage.showManufacturersCode ||
    configStore.productCard.showManufacturersCode;

  const shouldShowProductCodeInTags = (product) => {
    return accountStore.isRetailer && hasValue(product.productCode);
  };

  const shouldShowProductCodeUnderName = (product) => {
    return (
      !accountStore.isRetailer &&
      hasValue(product.productCode) &&
      configStore.productCard.showProductCode
    );
  };

  const shouldShowManufacturerCode = (product) => {
    return (
      accountStore.isRetailer &&
      showManufacturersCode &&
      hasValue(product.manufacturer_product_id)
    );
  };

  const renderProductCode = (product) => {
    if (shouldShowProductCodeUnderName(product)) {
      return (
        <span className="MyAccountOrder__products-code">
          {product.productCode}
        </span>
      );
    }
  };

  const renderProductTags = (product) => {
    const showProductCodeLabel = shouldShowProductCodeInTags(product);
    const showManufacturerCodeLabel = shouldShowManufacturerCode(product);

    return (
      <div className="MyAccountOrder__products-tags">
        {showProductCodeLabel && (
          <div className="MyAccountOrder__products-product-code">
            <span className="MyAccountOrder__products-product-code-label">
              <FormattedMessage
                {...globalTranslations.productCodeLabel}
                values={{
                  productCode: (
                    <span className="MyAccountOrder__products-product-code-value">
                      {product.productCode}
                    </span>
                  ),
                }}
              />
            </span>
          </div>
        )}
        {showManufacturerCodeLabel && (
          <div className="MyAccountOrder__products-manufacturer-code">
            <span className="MyAccountOrder__products-manufacturer-code-label">
              <FormattedMessage
                {...globalTranslations.manufacturerCodeLabel}
                values={{
                  manufacturerCode: (
                    <span className="MyAccountOrder__products-manufacturer-code-value">
                      {product.manufacturer_product_id}
                    </span>
                  ),
                }}
              />
            </span>
          </div>
        )}
      </div>
    );
  };

  const createProductImage = (url, id) => {
    return url
      ? ProductImageModel.create({
          id: 0,
          product_id: id,
          sizes: {
            small: url,
          },
        })
      : null;
  };

  const renderProductValueCol = (
    label,
    sizes,
    shouldRender,
    content,
    extraProps
  ) => {
    if (!shouldRender) {
      return isMobile ? null : <Col {...sizes} />;
    }

    return (
      <>
        {isMobile && renderProductHeaderCol(label)}
        <Col {...sizes} {...extraProps}>
          {content}
        </Col>
      </>
    );
  };

  const renderProductRow = (product, index) => {
    const isStructure = product.class === ProductClass.STRUCTURE;
    const isStructureChild =
      !!product.bundle_id &&
      structureBundleIds.has(product.bundle_id) &&
      !isStructure;
    const isFree = product.is_free_of_charge;
    const productLink = getProductLink(product);
    const isShippingCost = product.type === ProductTypeClass.SHIPPING_COST;
    const showTags = accountStore.isRetailer;
    const hasOrderedQuantity =
      !isShippingCost && hasValue(product.quantity_ordered);
    const hasDeliveredQuantity =
      !isShippingCost && hasValue(product.quantity_delivered);
    const hasTagValues =
      showTags &&
      !isShippingCost &&
      (shouldShowProductCodeInTags(product) ||
        shouldShowManufacturerCode(product));
    const hasUnitPrice =
      !isShippingCost && hasValue(product.unit_price_formatted);
    const productImage = (
      <ProductImage
        product={product}
        productImage={createProductImage(product.image_url, product.id)}
        size="small"
        forceAspectRatio={false}
      />
    );

    return (
      <Row
        key={`${product.id}-${product.bundle_id || 'no-bundle'}-${index}`}
        className={classNames('MyAccountOrder__product-row', {
          'MyAccountOrder__product-row--structure': isStructure,
          'MyAccountOrder__product-row--structure-child': isStructureChild,
          'MyAccountOrder__product-row--free': isFree,
        })}
      >
        {!isMobile && (
          <Col className="MyAccountOrder__products-image" xs="2" xl="1">
            {shouldRenderProductImage(product) &&
              (productLink ? (
                <MaybeExternalLink to={productLink}>
                  {productImage}
                </MaybeExternalLink>
              ) : (
                productImage
              ))}
          </Col>
        )}
        {isMobile && renderProductHeaderCol(OrderHeaders.name)}
        <Col xs="8" md={showTags ? '4' : '6'} xl={showTags ? '5' : '7'}>
          <div className="MyAccountOrder__products-name-wrapper">
            {isStructureChild && (
              <div className="MyAccountOrder__structure-child-label">
                <FormattedMessage
                  id="order.structureChildLabel"
                  defaultMessage="Included in this structure"
                />
              </div>
            )}
            {renderProductLink(product)}
            {!isShippingCost && renderProductCode(product)}
          </div>
          {product.variations && product.variations.length > 0 && (
            <ul
              className={
                isStructure && !isStructureChild
                  ? 'MyAccountOrder__variations--structure-indent'
                  : undefined
              }
            >
              {product.variations.map((variation, index) => (
                <li key={index}>{variation}</li>
              ))}
            </ul>
          )}
          {product.reference && (
            <div className="MyAccountOrder__products-reference">
              <FormattedMessage {...globalTranslations.referenceTitle} />
              {': ' + product.reference}
            </div>
          )}
          {product.warranty_in_months > 0 && (
            <div className="MyAccountOrder__products-warranty">
              <ProductWarranty warranty={product.warranty_in_months} />
            </div>
          )}
        </Col>
        {showTags &&
          renderProductValueCol(
            OrderHeaders.tags,
            { xs: '8', md: '2', xl: '2' },
            hasTagValues,
            renderProductTags(product)
          )}
        {renderProductValueCol(
          OrderHeaders.quantity,
          { xs: '8', md: '1' },
          hasOrderedQuantity,
          <>
            {product.quantity_ordered} {product.stock_unit}
          </>
        )}
        {renderProductValueCol(
          OrderHeaders.deliveredQuantity,
          { xs: '8', md: '1' },
          hasDeliveredQuantity,
          <>
            {product.quantity_delivered} {product.stock_unit}
          </>
        )}
        {renderProductValueCol(
          OrderHeaders.unitPrice,
          { xs: '8', md: '1' },
          hasUnitPrice,
          null,
          {
            dangerouslySetInnerHTML: { __html: product.unit_price_formatted },
          }
        )}
        {isMobile && renderProductHeaderCol(OrderHeaders.totalSum)}
        <Col
          xs="8"
          md="1"
          dangerouslySetInnerHTML={{ __html: product.total_price_formatted }}
        />
      </Row>
    );
  };

  const renderProductHeaderRow = () => {
    const showTags = accountStore.isRetailer;

    return (
      <Row className="MyAccountOrder__product-headers">
        {renderProductHeaderCol(OrderHeaders.image, { md: 2, xl: 1 })}
        {renderProductHeaderCol(
          OrderHeaders.name,
          showTags ? { md: 4, xl: 5 } : { md: 6, xl: 7 }
        )}
        {showTags &&
          renderProductHeaderCol(OrderHeaders.tags, { md: 2, xl: 2 })}
        {renderProductHeaderCol(OrderHeaders.quantity, { md: 1 })}
        {renderProductHeaderCol(OrderHeaders.deliveredQuantity, { md: 1 })}
        {renderProductHeaderCol(OrderHeaders.unitPrice, { md: 1 })}
        {renderProductHeaderCol(OrderHeaders.totalSum, { md: 1 })}
      </Row>
    );
  };

  return (
    <Row>
      {commonAlerts?.general?.length > 0 && (
        <Col xs={12}>{renderGeneralAlerts()}</Col>
      )}
      <Col xs={12}>
        <Card className="MyAccountOrderProducts">
          <CardHeader>
            <FormattedMessage {...globalTranslations.productsTitle} />
            <OrderCloneButton
              order={order}
              disabled={cartStore.state === RequestState.LOADING}
            />
          </CardHeader>
          <CardBody>
            <Container className="MyAccountOrder__products-container">
              <div className="MyAccountOrder__products">
                {!uiStore.isMobile && renderProductHeaderRow()}
                {orderedProducts.map((product, index) =>
                  renderProductRow(product, index)
                )}
              </div>
              <Row className="MyAccountOrder__products-totals-row">
                <Col
                  xs="12"
                  className="MyAccountOrder__products-totals-col d-flex justify-content-sm-end"
                >
                  <OrderTotals totals={order.allTotals} />
                </Col>
              </Row>
            </Container>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

MyAccountOrderProducts.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  order: modelOf(Order).isRequired,
};

export default inject(
  'accountStore',
  'cartStore',
  'configStore',
  'uiStore'
)(observer(MyAccountOrderProducts));
